// Per-fortnight diet rotation
// Maps a fortnight number (1..15) to 5 featured meal IDs.
// Fortnight 1 = weeks 1-2, fortnight 2 = weeks 3-4, etc. (after the 2-week window)
// Designed to take a participant on a 30-week diet journey through the library.
//
// Curation principles:
// - Fortnight 1-2: simple, comforting, foundation meals (build cooking confidence)
// - Fortnight 3-6: introduce variety, vegetarian options, planning ahead
// - Fortnight 7-10: international cuisines start appearing (one per fortnight)
// - Fortnight 11-15: a mix of repeat favourites and new challenges, plus celebration meals
//
// Each fortnight: 1 breakfast, 1-2 lunches, 1-2 dinners, sometimes a snack/smoothie.

import { DIET_MEALS } from './dietMeals';

export const WEEKLY_DIET_ROTATION = {
  1:  ['b2', 'l3', 'd1', 's1', 'sm1'],   // Veggie scramble, Lentil soup, Baked salmon, Apple & PB, Berry smoothie
  2:  ['b1', 'l8', 'd11', 's2', 'sm2'],  // Steel-cut oats, Turkey & avo, Veg lasagne, Hummus & sticks, Green detox
  3:  ['b3', 'l1', 'd2', 's3', 'sm3'],   // Greek yoghurt, Buddha bowl, Veggie stir-fry tofu, Yoghurt & honey, Choc protein
  4:  ['b4', 'l5', 'd3', 's4', 'sm4'],   // Avocado toast, Med chickpea, Chicken stew, Trail mix, Tropical
  5:  ['b6', 'l4', 'd5', 's5', 'l11'],   // Banana pancakes, Caesar wrap, Spag bol, Banana PB choc, Asian lettuce cups
  6:  ['b7', 'l9', 'd14', 'l12', 'sm1'], // Chia pudding, Tomato basil & cheese, Stuffed capsicum, Black bean salad, Berry smoothie
  7:  ['it1', 'it3', 'it6', 'it7', 'it10'],     // ITALIAN fortnight: Pomodoro, Minestrone, Bruschetta, Pesto, Caprese
  8:  ['fr1', 'fr2', 'fr6', 'fr7', 'fr8'],     // FRENCH: Ratatouille, Niçoise, Crêpes, Lyonnaise, Croque
  9:  ['es2', 'es3', 'es6', 'es8', 'es9'],     // SPANISH: Gazpacho, Tortilla, Lentejas, Chickpea spinach, Pan con tomate
  10: ['ru1', 'ru3', 'ru5', 'ru8', 'ru10'],    // RUSSIAN: Borscht, Buckwheat, Syrniki, Cucumber dill, Compote
  11: ['ua1', 'ua4', 'ua5', 'ua7', 'ua10'],    // UKRAINIAN: Borscht, Deruny, Beetroot walnut, Mlyntsi, Uzvar
  12: ['pt1', 'pt4', 'pt6', 'pt9', 'pt10'],    // PORTUGUESE: Caldo verde, Tomato rice, Bean stew, Tuna salad, Açorda
  13: ['kr1', 'kr2', 'kr5', 'kr7', 'kr10'],    // KOREAN: Bibimbap, Kimchi rice, Japchae, Cucumber salad, Egg roll
  14: ['d10', 'd13', 'd17', 'l14', 'l16'],     // Roast chicken Sunday, Salmon teriyaki, Zoodle bolognese, Greek + chicken, Caprese & prosciutto
  15: ['d8', 'd18', 'l15', 'fr3', 'it9'],      // Final fortnight: Cod, Halloumi quinoa, Pumpkin curry, Provençal chicken, Polpette
};

export const TOTAL_FORTNIGHTS = 15;

// Map a participant's current_week to a fortnight number (1..15)
// Returns null if currently in 2-week window (current_week <= 0)
export function fortnightForWeek(currentWeek) {
  if (!currentWeek || currentWeek < 1) return null;
  const f = Math.ceil(currentWeek / 2);
  return Math.min(Math.max(f, 1), TOTAL_FORTNIGHTS);
}

// Returns array of meal objects for a given fortnight
export function mealsForFortnight(fortnight) {
  const ids = WEEKLY_DIET_ROTATION[fortnight] || [];
  return ids.map(id => DIET_MEALS.find(m => m.id === id)).filter(Boolean);
}

// Friendly label for a fortnight — "Weeks 1-2", "Weeks 27-28", etc.
export function fortnightWeekLabel(fortnight) {
  const startWeek = (fortnight - 1) * 2 + 1;
  const endWeek = fortnight * 2;
  return `Weeks ${startWeek}–${endWeek}`;
}

// Optional cuisine theme description for each fortnight
export const FORTNIGHT_THEMES = {
  1:  'Foundations. Simple, comforting, achievable.',
  2:  'Building confidence. A little more variety.',
  3:  'Plant-forward week. Vegetarian and vegan staples.',
  4:  'Mediterranean focus. The mood diet with the most evidence behind it.',
  5:  'Hearty classics. Meals that fill the freezer.',
  6:  'Make-ahead and meal-prep. Reducing decision fatigue.',
  7:  'Italian fortnight. Pasta, risotto and quick classics.',
  8:  'French fortnight. Bistro favourites without the wine.',
  9:  'Spanish fortnight. Tapas and one-pan suppers.',
  10: 'Russian fortnight. Heritage flavours, alcohol-free.',
  11: 'Ukrainian fortnight. Comfort food traditions.',
  12: 'Portuguese fortnight. Coastal and rustic.',
  13: 'Korean fortnight. Bold flavours and probiotics.',
  14: 'Sunday-supper week. Feed the people you love.',
  15: 'Celebration fortnight. Your repertoire is built.',
};
